import React from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import {
  FiArrowRight,
  FiChevronRight,
  FiShield,
  FiStar,
  FiTruck,
  FiZap,
} from "react-icons/fi";

import styles from "../../styles/pages/home.module.css";
import HomeProducts from "../products/HomeProducts";

const dealCategories = [
  { title: "Apparel & Accessories", subtitle: "Curated picks", icon: "👗", link: "/category/apparel-accessories" },
  { title: "Shoes", subtitle: "Curated picks", icon: "👟", link: "/category/shoes" },
  { title: "Luggage & Bags", subtitle: "Curated picks", icon: "👜", link: "/category/luggage-bags" },
  { title: "Watches", subtitle: "Curated picks", icon: "⌚", link: "/category/watches" },
  { title: "Beauty", subtitle: "Curated picks", icon: "💄", link: "/category/beauty" },
];

const quickLinks = [
  { title: "Trending", subtitle: "Popular picks", icon: "🔥", link: "/trending" },
  { title: "Best Rated", subtitle: "Community feedback", icon: "⭐", link: "/best-rated" },
  { title: "Featured", subtitle: "Editor-selected picks", icon: "✨", link: "/featured" },
  { title: "Explore All", subtitle: "Browse categories", icon: "🛍️", link: "/products" },
];

const Home: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>
          Mohit's Shopping App | Diwali Deals, Trending Products & Best Offers
        </title>

        <meta
          name="description"
          content="Discover carefully selected Diwali deals, trending products, highly rated products and budget-friendly offers across electronics, fashion, beauty, home and more."
        />

        <meta
          name="keywords"
          content="Diwali deals, Diwali offers, best deals India, trending products, best rated products, shopping deals, Amazon deals"
        />

        <meta
          property="og:title"
          content="Mohit's Shopping App | Diwali Deals & Best Offers"
        />

        <meta
          property="og:description"
          content="Explore festive deals, trending products and highly rated picks."
        />

        <meta
          property="og:url"
          content="https://mohit-online-shopping-testing.netlify.app/"
        />
      </Helmet>

      <main className={styles.home}>
        {/* HERO */}
        <section className={styles.hero}>
          <div className={styles.heroGlowOne} />
          <div className={styles.heroGlowTwo} />

          <div className={styles.heroContainer}>
            <div className={styles.heroContent}>
              <div className={styles.eyebrow}>
                <span className={styles.liveDot} />
                Festive shopping starts here
              </div>

              <h1>
                Find the deals
                <span> worth clicking.</span>
              </h1>

              <p className={styles.heroDescription}>
                Discover hand-picked products, popular picks, highly rated
                items and festive offers — all in one place.
              </p>

              <div className={styles.heroActions}>
                <Link
                  to="/diwali-deals"
                  className={`${styles.primaryButton} ${styles.heroButton}`}
                >
                  Explore Diwali Deals
                  <FiArrowRight />
                </Link>

                <Link
                  to="/best-sellers"
                  className={styles.secondaryButton}
                >
                  View Best Sellers
                </Link>
              </div>

              <div className={styles.heroTrust}>
                <div>
                  <FiStar />
                  <span>Highly rated picks</span>
                </div>

                <div>
                  <FiZap />
                  <span>Trending deals</span>
                </div>

                <div>
                  <FiShield />
                  <span>Compare before buying</span>
                </div>
              </div>
            </div>

            <div className={styles.heroVisual}>
              <div className={styles.festivalOrb}>
                <span>दीपावली</span>
                <strong>DEALS</strong>
                <small>FESTIVE PICKS</small>
              </div>

              <div className={`${styles.floatingCard} ${styles.cardOne}`}>
                <span>🔥</span>
                <div>
                  <strong>Trending</strong>
                  <small>Popular right now</small>
                </div>
              </div>

              <div className={`${styles.floatingCard} ${styles.cardTwo}`}>
                <span>⭐</span>
                <div>
                  <strong>Best Rated</strong>
                  <small>Top customer ratings</small>
                </div>
              </div>

              <div className={`${styles.floatingCard} ${styles.cardThree}`}>
                <span>💰</span>
                <div>
                  <strong>Smart Picks</strong>
                  <small>Worth checking out</small>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CATEGORY STRIP */}
        <section className={styles.section}>
          <div className={styles.sectionContainer}>
            <div className={styles.sectionHeading}>
              <div>
                <span className={styles.sectionKicker}>SHOP BY MOOD</span>
                <h2>What are you shopping for?</h2>
              </div>

              <Link to="/diwali-deals" className={styles.viewAll}>
                View all <FiChevronRight />
              </Link>
            </div>

            <div className={styles.categoryGrid}>
              {dealCategories.map((category) => (
                <Link
                  key={category.title}
                  to={category.link}
                  className={styles.categoryCard}
                >
                  <div className={styles.categoryIcon}>{category.icon}</div>

                  <div>
                    <h3>{category.title}</h3>
                    <p>{category.subtitle}</p>
                  </div>

                  <FiArrowRight className={styles.cardArrow} />
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* QUICK DEALS */}
        <section className={styles.quickSection}>
          <div className={styles.sectionContainer}>
            <div className={styles.sectionHeading}>
              <div>
                <span className={styles.sectionKicker}>QUICK PICKS</span>
                <h2>Shop by your budget</h2>
              </div>
            </div>

            <div className={styles.quickGrid}>
              {quickLinks.map((item) => (
                <Link
                  to={item.link}
                  key={item.title}
                  className={styles.quickCard}
                >
                  <span>{item.icon}</span>

                  <div>
                    <strong>{item.title}</strong>
                    <small>{item.subtitle}</small>
                  </div>

                  <FiArrowRight />
                </Link>
              ))}
            </div>
          </div>
        </section>

        <HomeProducts />

        {/* FEATURED MESSAGE */}
        <section className={styles.featureSection}>
          <div className={styles.featureContainer}>
            <div className={styles.featureText}>
              <span className={styles.sectionKicker}>SMART SHOPPING</span>

              <h2>
                Don't just find a product.
                <span> Find the right one.</span>
              </h2>

              <p>
                Compare product details, ratings, popularity and feedback before heading
                to the marketplace. We make discovery easier so you can shop
                with more confidence.
              </p>

              <Link
                to="/best-rated"
                className={styles.primaryButton}
              >
                Explore top rated products
                <FiArrowRight />
              </Link>
            </div>

            <div className={styles.featureStats}>
              <div>
                <FiStar />
                <strong>Ratings</strong>
                <span>See highly reviewed picks</span>
              </div>

              <div>
                <FiZap />
                <strong>Trending</strong>
                <span>Discover popular products</span>
              </div>

              <div>
                <FiTruck />
                <strong>Marketplace</strong>
                <span>Check the marketplace details before buying</span>
              </div>
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className={styles.finalCta}>
          <div>
            <span>🎆 FESTIVE SEASON</span>

            <h2>
              Your next great deal
              <br />
              might be one click away.
            </h2>

            <p>
              Explore our latest curated picks and discover something worth
              buying.
            </p>

            <Link
              to="/diwali-deals"
              className={styles.primaryButton}
            >
              Start exploring
              <FiArrowRight />
            </Link>
          </div>
        </section>
      </main>
    </>
  );
};

export default Home;















// // // import React from 'react';
// import { Container } from 'react-bootstrap';
// import { Helmet } from 'react-helmet';
// import Styles from '../../styles/common/pages.module.css'
// import Style from '../../styles/pages/home.module.css'
// import Banner from '../../component/pages/Banner';
// import SecBanner from '../../component/pages/Banner-two';

// const Home = () => {

//   return (
//     <>
//       <Helmet>
//         <title>Home - Mohit's Shopping App</title>
//         <meta
//           name="description"
//           content="Discover the best deals on home goods, kitchenware, clothing, jewelry, and tech gadgets at Mohit's Shopping App."
//         />
//         <meta name="viewport" content="width=device-width, initial-scale=1" />
//         <meta property="og:title" content="Home - Mohit's Shopping App" />
//         <meta
//           property="og:description"
//           content="Discover the best deals on home goods, kitchenware, clothing, jewelry, and tech gadgets at Mohit's Shopping App."
//         />
//         <meta
//           property="og:image"
//           content={`${process.env.PUBLIC_URL}/favicon.png`}
//         />
//         <meta
//           property="og:url"
//           content="https://mohit-online-shopping.netlify.app/"
//         />
//       </Helmet>
//       {/* Main Heading of the Home Page */}
//       <h1 className="visually-hidden">Home - Online Shopping App & Website</h1>
//       {/* This is the First Section of Home Page */}
//       <section className={Styles.headSec}>
//         <Container>
//           <h2>Home</h2>
//         </Container>
//       </section>
//       <section className={Style.bannerSec}>
//         <Banner />
//       </section>
//       <section className={Style.bannerthrd}>
//         <SecBanner />
//       </section>
//       <section>
//         <Container>
//           <p>Welcome! We're working hard to bring you an amazing experience. Our homepage is currently under construction, so please check back soon for updates. Thank you for your patience!</p>
//         </Container>
//       </section>
//     </>
//   );
// };

// export default Home;
